import { clsx } from 'clsx'
import { html, nothing, PropertyValues } from 'lit'
import { customElement, property, query, state } from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js'

import { CommonFormElementSize } from '@/common/types'
import { translate } from '@/localization/localization'

import { InputBaseElement } from '../input/input-base-element'

/**
 * `dap-ds-number-input`
 * @summary A number input component.
 * @element dap-ds-number-input
 * @title - Number input
 *
 * @property {string} label - The label of the input.
 * @property {string} placeholder - The placeholder of the input.
 * @property {string} description - The description of the input.
 * @property {string} tooltip - The tooltip of the input.
 * @property {'top' | 'right' | 'bottom' | 'left'} tooltipPlacement - The tooltip placement of the input.
 * @property {'success' | 'error'} status - The status of the input. Can be `success` or `error`.
 * @property {'xs' | 'sm' | 'sm'} size - The size of the input. Default is `sm`. Can be `sm` or `lg`.
 * @property {string} name - The name of the input.
 * @property {string} value - The value of the input.
 * @property {string} thousandSeparator - The thousand separator of the input.
 * @property {string} decimalSeparator - The decimal separator of the input.
 * @property {number} decimalScale - The decimal scale of the input.
 * @property {string} allowNegative - The allow negative state of the input.
 * @property {string} allowDecimal - The allow decimal state of the input.
 * @property {boolean} disabled - The disabled state of the input. Default is false.
 * @property {boolean} required - The required state of the input. Default is false.
 * @property {boolean} readonly - The readonly state of the input. Default is false.
 * @property {boolean} autofocus - The autofocus state of the input. Default is false.
 * @property {number} min - The minimum value of the number input.
 * @property {number} max - The maximum value of the number input.
 * @property {number} step - The step value of the number input.
 * @property {string} feedback - The feedback of the input.
 * @property {'negative' | 'positive' | 'warning'} feedbackType - The feedback type of the input. Can be `negative`, `positive`, or `warning`.
 * @property {boolean} loading - The loading state of the input. Default is false.
 * @property {boolean} optional - The optional state of the input.
 * @property {string} optionalLabel - The optional label of the input.
 * @property {boolean} subtle - The weight of the label. Default is `false`
 * @property {boolean} disableEnter - Disables the enter key from being used to submit the form.
 * @property {boolean} hideControls - Hides the increment and decrement buttons.
 *
 * @event {{ value: string }} dds-change - Fired when the input value changes.
 * @event {{ value: string }} dds-input - Fired when the input value changes.
 * @event {{ value: string, originalEvent: Event }} dds-keydown - Fired when a key is pressed down.
 * @event {{ void }} dds-blur - Fired when the input loses focus.
 * @event {{ void }} dds-focus - Emitted when the input gains focus.
 *
 * @slot feedback-icon - The custom icon of the feedback.
 *
 * @cssproperty --dds-input-height - The height of the input. Default is var(--dds-spacing-1000).
 * @cssproperty --dds-input-padding - The padding of the input. Default is var(--dds-spacing-300).
 * @cssproperty --dds-input-font-size - The font size of the input. Default is var(--dds-font-base).
 * @cssproperty --dds-input-border-color - The border color of the input. Default is var(--dds-border-neutral-base).
 * @cssproperty --dds-input-background-color - The background color of the input. Default is var(--dds-fields-background-default).
 * @cssproperty --dds-input-text-color - The text color of the input. Default is var(--dds-text-neutral-base).
 * @cssproperty --dds-input-border-radius - The border radius of the input. Default is var(--dds-radius-base).
 * @cssproperty --dds-input-border-width - The border width of the input. Default is var(--dds-border-width-base).
 * @cssproperty --dds-input-disabled-background - The background color of the disabled input. Default is var(--dds-fields-background-disabled).
 * @cssproperty --dds-input-disabled-text - The text color of the disabled input. Default is var(--dds-text-neutral-disabled).
 * @cssproperty --dds-input-readonly-background - The background color of the readonly input. Default is var(--dds-fields-background-read-only).
 * @cssproperty --dds-input-readonly-text - The text color of the readonly input. Default is var(--dds-text-neutral-subtle).
 * @cssproperty --dds-input-success-border - The border color of the success input. Default is var(--dds-border-positive-base).
 * @cssproperty --dds-input-error-border - The border color of the error input. Default is var(--dds-border-negative-base).
 * @cssproperty --dds-input-addon-background - The background color of the input addon. Default is var(--dds-background-ui-neutral-enabled).
 * @cssproperty --dds-input-addon-success-background - The background color of the success input addon. Default is var(--dds-background-ui-positive-enabled).
 * @cssproperty --dds-input-addon-error-background - The background color of the error input addon. Default is var(--dds-background-ui-negative-enabled).
 *
 * @csspart postfix - The postfix of the input.
 * @csspart decrement - The decrement button of the input.
 * @csspart increment - The increment button of the input.
 */
@customElement('dap-ds-number-input')
export default class DapDSNumberInput extends InputBaseElement {
  constructor() {
    super()
    this.manualValueSet = true
  }

  @query('input')
  private readonly inputElement!: HTMLInputElement

  @property({ type: String }) thousandSeparator = ''
  @property({ type: String }) decimalSeparator = ','
  @property({ type: Number }) decimalScale?: number
  @property({ type: String }) allowNegative = 'true'
  @property({ type: String }) allowDecimal = 'true'
  @property({ type: Boolean }) hideControls = false

  @state() private formattedValue = ''

  get _thousandSeparator() {
    //escape regex control characters with \
    return this.thousandSeparator?.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  }

  firstUpdated() {
    this.inputMode =
      this.inputMode || (this.allowDecimal === 'true' ? 'decimal' : 'numeric')

    this.inputElement.addEventListener('input', (e: Event) =>
      this.handleInput(e as InputEvent),
    )

    this.inputElement.addEventListener('keydown', (e: KeyboardEvent) =>
      this.handleKeydown(e),
    )

    // Handle initial value
    if (this.value !== undefined) {
      this.formattedValue = this.getMaskedValue(this.value)
      this.inputElement.value = this.formattedValue
    }
  }

  private handleKeydown(event: KeyboardEvent) {
    if (this.disabled || this.readonly) return

    if (event.key === 'ArrowUp') {
      event.preventDefault()
      this._increment()
    } else if (event.key === 'ArrowDown') {
      event.preventDefault()
      this._decrement()
    }
  }

  private getMaskedValue(value: string | number): string {
    if (!value && value !== 0) return ''

    let result = value

    // if the value is a number and it has a decimal point, convert it to a string and replace the . with the decimal separator
    if (typeof result === 'number' && result.toString().includes('.')) {
      result = result.toString().replace('.', this.decimalSeparator)
    }

    // Handle negative numbers
    if (this.allowNegative === 'true' && value.toString().startsWith('-')) {
      result = '-' + this.getMaskedValue(value.toString().substring(1))
    } else if (
      this.allowNegative === 'false' &&
      value.toString().startsWith('-')
    ) {
      result = value.toString().substring(1)
    }

    // Apply thousand separator
    if (this.thousandSeparator) {
      const parts = result.toString().split(this.decimalSeparator)
      parts[0] = parts[0].replace(
        /\B(?=(\d{3})+(?!\d))/g,
        this.thousandSeparator,
      )
      result = parts.join(this.decimalSeparator)
    }

    // Apply decimal scale
    if (this.allowDecimal === 'true' && this.decimalScale !== undefined) {
      const parts = result.toString().split(this.decimalSeparator)
      if (parts[1]) {
        parts[1] = parts[1].slice(0, this.decimalScale)
        result = parts.join(this.decimalSeparator)
      }
    } else if (this.allowDecimal === 'false') {
      const parts = result.toString().split(this.decimalSeparator)
      result = parts[0]
    }

    return result.toString()
  }

  private isBackspaceOrDelete(event: InputEvent): boolean {
    return (
      event.inputType === 'deleteContentBackward' ||
      event.inputType === 'deleteContentForward'
    )
  }

  private handleEmptyValue() {
    this.value = ''
    this.formattedValue = ''
    this.inputElement.value = ''
    this.emit('dds-change', {
      value: '',
      formattedValue: '',
    })
  }

  private createNumericRegex(): RegExp {
    if (this.allowDecimal === 'true') {
      const escapedDecimalSeparator = this.decimalSeparator.replace(
        /[.*+?^${}()|[\]\\]/g,
        '\\$&',
      )
      return this.allowNegative === 'true'
        ? new RegExp(`^-?\\d*${escapedDecimalSeparator}?\\d*$`)
        : new RegExp(`^\\d*${escapedDecimalSeparator}?\\d*$`)
    }
    return this.allowNegative === 'true' ? /^-?\d*$/ : /^\d*$/
  }

  private enforceDecimalScale(value: string): string {
    if (this.decimalScale === undefined || this.allowDecimal !== 'true') {
      return value
    }

    const parts = value.split(this.decimalSeparator)
    if (parts[1] && parts[1].length > this.decimalScale) {
      parts[1] = parts[1].slice(0, this.decimalScale)
      return parts.join(this.decimalSeparator)
    }
    return value
  }

  private convertToNumericValue(value: string): number | string {
    if (!value) return value

    const normalizedValue = value.replace(this.decimalSeparator, '.')
    const numericValue = parseFloat(normalizedValue)
    return isNaN(numericValue) ? value : numericValue
  }

  private handleInput(event: InputEvent) {
    const input = event.target as HTMLInputElement
    let value = input.value

    // Remove thousand separators
    if (this.thousandSeparator) {
      value = value.replace(new RegExp(this._thousandSeparator, 'g'), '')
    }

    // Handle empty value
    if (value === '') {
      this.handleEmptyValue()
      return
    }

    // Check for negative numbers if not allowed
    if (this.allowNegative === 'false' && value.includes('-')) {
      input.value = value.replace('-', '')
      this.formattedValue = value.replace('-', '')
      return
    }

    // Validate numeric input
    const numericRegex = this.createNumericRegex()
    if (!numericRegex.test(value) && !this.isBackspaceOrDelete(event)) {
      input.value = this.formattedValue
      return
    }

    // Enforce decimal scale
    value = this.enforceDecimalScale(value)

    // Convert and update value
    const numericValue = this.convertToNumericValue(value)
    this.value = numericValue
    this.formattedValue = this.getMaskedValue(value)
    input.value = this.formattedValue

    this.emit('dds-change', {
      value: this.value,
      formattedValue: this.formattedValue,
    })
  }

  protected updated(changedProperties: PropertyValues) {
    if (changedProperties.has('value')) {
      this.inputElement.value = this.formattedValue
    }
  }

  formResetCallback() {
    super.formResetCallback()
    // Reset the formatted value to match the reset value
    this.formattedValue = this.getMaskedValue(this.value || '')
    this.inputElement.value = this.formattedValue
  }

  private _decrement() {
    if (this.disabled || this.readonly) return

    const currentValue = this.value
      ? parseFloat(this.value.toString().replace(this._thousandSeparator, ''))
      : 0
    const step =
      typeof this.step === 'number'
        ? this.step
        : parseFloat(this.step?.toString() ?? '1')

    // Calculate new value
    let newValue = currentValue - step

    // Apply min constraint if defined
    if (this.min !== undefined && newValue < this.min) {
      newValue = this.min
    }

    // Update the value
    this.value = newValue
    this.formattedValue = this.getMaskedValue(newValue)
    this.inputElement.value = this.formattedValue

    this.emit('dds-change', {
      value: this.value,
      formattedValue: this.formattedValue,
    })
  }

  private _increment() {
    if (this.disabled || this.readonly) return

    const currentValue = this.value
      ? parseFloat(this.value.toString().replace(this.thousandSeparator, ''))
      : 0
    const step =
      typeof this.step === 'number'
        ? this.step
        : parseFloat(this.step?.toString() ?? '1')

    // Calculate new value
    let newValue = currentValue + step

    // Apply max constraint if defined
    if (this.max !== undefined && newValue > this.max) {
      newValue = this.max
    }

    // Update the value
    this.value = newValue
    this.formattedValue = this.getMaskedValue(newValue)
    this.inputElement.value = this.formattedValue

    this.emit('dds-change', {
      value: this.value,
      formattedValue: this.formattedValue,
    })
  }

  render() {
    return this.renderInput(
      html` <span
        part="postfix"
        class=${clsx('input__addon-postfix number-input')}>
        ${!(this.disabled || this.readonly) && !this.hideControls
          ? html` <dap-ds-stack direction="row" tabindex="-1" spacing="300">
              ${this.loading
                ? html`<dap-ds-spinner size="lg"></dap-ds-spinner>`
                : nothing}
              <dap-ds-icon-button
                part="decrement"
                size=${ifDefined(this.size as CommonFormElementSize)}
                @click=${this._decrement}
                aria-label="${translate('decrease')}"
                .disabled=${this.disabled}>
                <dap-ds-icon size="lg" name="subtract-line"></dap-ds-icon>
              </dap-ds-icon-button>
              <dap-ds-icon-button
                part="increment"
                size=${ifDefined(this.size as CommonFormElementSize)}
                @click=${this._increment}
                aria-label="${translate('increase')}"
                .disabled=${this.disabled}>
                <dap-ds-icon size="lg" name="add-line"></dap-ds-icon>
              </dap-ds-icon-button>
            </dap-ds-stack>`
          : ``}
      </span>`,
    )
  }
}
